import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Col, Form as BsForm, Row, Spinner } from 'react-bootstrap';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import '../settings/commonstyle.css';
import '../users/userlist.css';
import '../tasks/tasklist.css';
import './credentialVault.css';

import {
  deriveKeys,
  prepareMasterPasswordRotation,
  preparePersonalVaultPasswordRotation,
  preparePersonalVaultSetup,
  base64ToBytes,
  zeroBytes,
  isCorporateVaultAccount,
} from '../../crypto/credentialVaultCrypto';
import {
  getVaultCryptoStatus,
  getVaultSalt,
  unlockVaultApi,
  resetMasterPasswordApi,
  getVaultCryptoActivities,
  setupPersonalVaultApi,
  getPersonalVaultSalt,
  unlockPersonalVaultApi,
  resetPersonalVaultPasswordApi,
} from '../../services/CredentialVaultService';
import { useCredentialVaultCrypto } from './VaultCryptoProvider';

const VaultSettingsSchema = Yup.object().shape({
  currentMasterPassword: Yup.string().required('Current master password is required'),
  newMasterPassword: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('New master password is required'),
  confirmNewMasterPassword: Yup.string()
    .oneOf([Yup.ref('newMasterPassword'), null], 'Passwords must match')
    .required('Confirm the new master password'),
});

const PersonalVaultPasswordSchema = Yup.object().shape({
  currentPersonalPassword: Yup.string().required('Current personal password is required'),
  newPersonalPassword: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('New personal password is required'),
  confirmNewPersonalPassword: Yup.string()
    .oneOf([Yup.ref('newPersonalPassword'), null], 'Passwords must match')
    .required('Confirm the new personal password'),
});

const VAULT_CRYPTO_ACTION_LABELS = {
  vault_initialized: 'Credentials Vault initialized',
  master_password_changed: 'Master password changed',
};

function formatVaultCryptoActivity(row) {
  return VAULT_CRYPTO_ACTION_LABELS[row.action] || row.action;
}

export default function VaultSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vaultInitialized, setVaultInitialized] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [personalSetupPw, setPersonalSetupPw] = useState('');
  const [personalSetupPw2, setPersonalSetupPw2] = useState('');
  const [showPersonalSetupPw, setShowPersonalSetupPw] = useState(false);
  const [showPersonalSetupPw2, setShowPersonalSetupPw2] = useState(false);
  const [showPersonalChangeCurrentPw, setShowPersonalChangeCurrentPw] = useState(false);
  const [showPersonalChangeNewPw, setShowPersonalChangeNewPw] = useState(false);
  const [showPersonalChangeConfirmPw, setShowPersonalChangeConfirmPw] = useState(false);
  const [showMasterCurrentPw, setShowMasterCurrentPw] = useState(false);
  const [showMasterNewPw, setShowMasterNewPw] = useState(false);
  const [showMasterConfirmPw, setShowMasterConfirmPw] = useState(false);
  const [personalSetupBusy, setPersonalSetupBusy] = useState(false);
  const [personalSetupError, setPersonalSetupError] = useState('');
  const isCorporateAccount = useMemo(() => isCorporateVaultAccount(), []);
  const { personalVaultInitialized, refreshPersonalVaultStatus, retainPersonalVaultDekAfterSetup, clearPersonalVaultSession } =
    useCredentialVaultCrypto();

  const goToList = () => navigate('/credential-vault/credentialVaultList');

  const loadStatus = useCallback(async () => {
    const res = await getVaultCryptoStatus();
    const ok = res.data?.success && res.data?.data?.vaultInitialized === true;
    setVaultInitialized(!!ok);
    return !!ok;
  }, []);

  const loadHistory = useCallback(async () => {
    if (!isCorporateAccount) return;
    setHistoryError('');
    setHistoryLoading(true);
    try {
      const res = await getVaultCryptoActivities({ page: 1, limit: 50 });
      if (res.data?.success && Array.isArray(res.data?.data)) {
        setHistoryRows(res.data.data);
      } else {
        setHistoryRows([]);
        setHistoryError(res.data?.message || 'Could not load history');
      }
    } catch (e) {
      setHistoryRows([]);
      setHistoryError(e?.response?.data?.message || 'Could not load history');
    } finally {
      setHistoryLoading(false);
    }
  }, [isCorporateAccount]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const ok = await loadStatus();
        if (!cancelled && ok && isCorporateAccount) {
          await loadHistory();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadStatus, loadHistory, isCorporateAccount]);

  useEffect(() => {
    if (isCorporateAccount) {
      void refreshPersonalVaultStatus();
    }
  }, [isCorporateAccount, refreshPersonalVaultStatus]);

  const showHistoryColumn = isCorporateAccount && vaultInitialized;

  const handlePersonalVaultSetup = async (e) => {
    e.preventDefault();
    setPersonalSetupError('');
    setPersonalSetupBusy(true);
    try {
      const bundle = await preparePersonalVaultSetup(personalSetupPw, personalSetupPw2);
      await setupPersonalVaultApi({
        kdfSalt: bundle.kdfSalt,
        authHash: bundle.authHash,
        encryptedDek: bundle.encryptedDek,
      });
      retainPersonalVaultDekAfterSetup(bundle.dek);
      toast.success('Personal vault created. You can add personal credentials from the credentials list.');
      setPersonalSetupPw('');
      setPersonalSetupPw2('');
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || 'Could not create personal vault';
      setPersonalSetupError(msg);
    } finally {
      setPersonalSetupBusy(false);
    }
  };

  const pageChrome = (children) => (
    <div className="credential-vault-form-page">
      <ToastContainer position="top-right" />
      <button type="button" className="back-button" onClick={goToList}>
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <Row style={{ marginBottom: 0 }} className="credential-vault-settings-page-row">
        <Col xl={12}>{children}</Col>
      </Row>
      <InternalFooter />
    </div>
  );

  if (loading) {
    return pageChrome(
      <div className="credential-vault-settings-layout credential-vault-settings-layout--wide">
        <Card className="user-list-card credential-form-card">
          <Card.Body
            className="d-flex align-items-center justify-content-center credential-form-loading"
            style={{ minHeight: '320px', padding: '1.5rem' }}
          >
            <Spinner animation="border" role="status" />
          </Card.Body>
        </Card>
      </div>
    );
  }

  const settingsCardBodyStyle = { minHeight: '320px', overflow: 'visible', padding: '1.5rem' };

  return pageChrome(
    <div className="credential-vault-settings-layout credential-vault-settings-layout--wide credential-vault-settings-layout--split">
        <Row className="g-4 align-items-stretch credential-vault-settings-split-row">
          <Col xl={showHistoryColumn ? 8 : 12} lg={showHistoryColumn ? 8 : 12} md={12}>
            <Card className="user-list-card credential-form-card credential-vault-settings-main-card h-100">
              <Card.Body style={settingsCardBodyStyle}>
                {isCorporateAccount ? (
                  <section className="credential-vault-settings-password-block mb-0">
                    {!vaultInitialized ? (
                      <Alert variant="light" className="credential-vault-settings-message mb-4">
                        The organization Credentials Vault is not initialized yet. You can still create and use your
                        personal vault below. Corporate shared credentials will be available after the organization vault
                        is set up from the{' '}
                        <Link to="/credential-vault/credentialVaultList">Corporate credentials</Link> list.
                      </Alert>
                    ) : null}
                    {personalVaultInitialized ? (
                      <>
                        <h2 className="credential-vault-settings-section-title mb-2">
                          Change personal vault password
                        </h2>
                        <p className="credential-vault-settings-section-lead mb-4">
                          Enter your current personal vault password, then choose a new one. You do not need to unlock your
                          personal vault on this device first.
                        </p>
                        <Formik
                              initialValues={{
                                currentPersonalPassword: '',
                                newPersonalPassword: '',
                                confirmNewPersonalPassword: '',
                              }}
                              validationSchema={PersonalVaultPasswordSchema}
                              onSubmit={async (values, { setSubmitting, resetForm }) => {
                                try {
                                  const saltRes = await getPersonalVaultSalt();
                                  if (!saltRes.data?.success) {
                                    throw new Error(saltRes.data?.message || 'Could not load personal vault salt');
                                  }
                                  const kdfSalt = saltRes.data?.data?.kdfSalt;
                                  if (!kdfSalt) {
                                    throw new Error(saltRes.data?.message || 'Personal vault not initialized');
                                  }
                                  const saltBytes = base64ToBytes(kdfSalt);
                                  const { kek, authHash } = await deriveKeys(values.currentPersonalPassword, saltBytes);
                                  try {
                                    const unlockRes = await unlockPersonalVaultApi(authHash);
                                    if (!unlockRes.data?.success) {
                                      throw new Error(
                                        unlockRes.data?.message || 'Invalid current personal vault password'
                                      );
                                    }
                                    const enc = unlockRes.data?.data?.encryptedDek;
                                    if (!enc) {
                                      throw new Error(unlockRes.data?.message || 'Unlock failed');
                                    }
                                    const rotation = await preparePersonalVaultPasswordRotation(
                                      values.currentPersonalPassword,
                                      values.newPersonalPassword,
                                      values.confirmNewPersonalPassword,
                                      kdfSalt,
                                      enc
                                    );
                                    const resetRes = await resetPersonalVaultPasswordApi({
                                      currentAuthHash: rotation.currentAuthHash,
                                      newAuthHash: rotation.newAuthHash,
                                      newEncryptedDek: rotation.newEncryptedDek,
                                    });
                                    if (!resetRes.data?.success) {
                                      throw new Error(
                                        resetRes.data?.message || 'Failed to update personal vault password'
                                      );
                                    }
                                    toast.success('Personal vault password updated');
                                    resetForm();
                                    clearPersonalVaultSession();
                                  } finally {
                                    await zeroBytes(kek);
                                  }
                                } catch (err) {
                                  const msg =
                                    err?.response?.data?.message ||
                                    err?.message ||
                                    'Failed to reset personal vault password';
                                  toast.error(msg);
                                } finally {
                                  setSubmitting(false);
                                }
                              }}
                            >
                              {({ isSubmitting }) => (
                                <FormikForm autoComplete="off">
                                  <Row>
                                    <Col md={12} className="mb-2">
                                      <div className="form-group mb-3">
                                        <label htmlFor="vault-current-personal" className="text-label">
                                          Current personal password
                                        </label>
                                        <div className="position-relative password-input-wrapper">
                                          <Field
                                            id="vault-current-personal"
                                            type={showPersonalChangeCurrentPw ? 'text' : 'password'}
                                            name="currentPersonalPassword"
                                            autoComplete="off"
                                            className="form-control"
                                            placeholder="Current personal password"
                                          />
                                          <button
                                            type="button"
                                            className="password-toggle-btn"
                                            disabled={isSubmitting}
                                            onClick={() => setShowPersonalChangeCurrentPw((v) => !v)}
                                            aria-label={
                                              showPersonalChangeCurrentPw ? 'Hide password' : 'Show password'
                                            }
                                          >
                                            <i
                                              className={`fas ${showPersonalChangeCurrentPw ? 'fa-eye-slash' : 'fa-eye'}`}
                                              aria-hidden
                                            />
                                          </button>
                                        </div>
                                        <ErrorMessage
                                          name="currentPersonalPassword"
                                          component="div"
                                          className="text-danger small"
                                        />
                                      </div>
                                    </Col>
                                    <Col lg={6} className="mb-2">
                                      <div className="form-group mb-3">
                                        <label htmlFor="vault-new-personal" className="text-label">
                                          New personal password
                                        </label>
                                        <div className="position-relative password-input-wrapper">
                                          <Field
                                            id="vault-new-personal"
                                            type={showPersonalChangeNewPw ? 'text' : 'password'}
                                            name="newPersonalPassword"
                                            autoComplete="off"
                                            className="form-control"
                                            placeholder="New personal password"
                                          />
                                          <button
                                            type="button"
                                            className="password-toggle-btn"
                                            disabled={isSubmitting}
                                            onClick={() => setShowPersonalChangeNewPw((v) => !v)}
                                            aria-label={showPersonalChangeNewPw ? 'Hide password' : 'Show password'}
                                          >
                                            <i
                                              className={`fas ${showPersonalChangeNewPw ? 'fa-eye-slash' : 'fa-eye'}`}
                                              aria-hidden
                                            />
                                          </button>
                                        </div>
                                        <ErrorMessage
                                          name="newPersonalPassword"
                                          component="div"
                                          className="text-danger small"
                                        />
                                      </div>
                                    </Col>
                                    <Col lg={6} className="mb-2">
                                      <div className="form-group mb-3">
                                        <label htmlFor="vault-confirm-personal" className="text-label">
                                          Confirm new personal password
                                        </label>
                                        <div className="position-relative password-input-wrapper">
                                          <Field
                                            id="vault-confirm-personal"
                                            type={showPersonalChangeConfirmPw ? 'text' : 'password'}
                                            name="confirmNewPersonalPassword"
                                            autoComplete="off"
                                            className="form-control"
                                            placeholder="Confirm new personal password"
                                          />
                                          <button
                                            type="button"
                                            className="password-toggle-btn"
                                            disabled={isSubmitting}
                                            onClick={() => setShowPersonalChangeConfirmPw((v) => !v)}
                                            aria-label={
                                              showPersonalChangeConfirmPw ? 'Hide password' : 'Show password'
                                            }
                                          >
                                            <i
                                              className={`fas ${showPersonalChangeConfirmPw ? 'fa-eye-slash' : 'fa-eye'}`}
                                              aria-hidden
                                            />
                                          </button>
                                        </div>
                                        <ErrorMessage
                                          name="confirmNewPersonalPassword"
                                          component="div"
                                          className="text-danger small"
                                        />
                                      </div>
                                    </Col>
                                  </Row>
                                  <div className="text-end toolbar toolbar-bottom pt-3">
                                    <button
                                      type="submit"
                                      className="btn credential-vault-settings-submit-btn"
                                      disabled={isSubmitting}
                                    >
                                      {isSubmitting ? (
                                        <>
                                          <Spinner animation="border" size="sm" className="me-2" />
                                          Updating…
                                        </>
                                      ) : (
                                        <>
                                          <i className="fas fa-key me-2" aria-hidden />
                                          Update personal password
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </FormikForm>
                              )}
                            </Formik>
                      </>
                    ) : (
                      <>
                        <h2 className="credential-vault-settings-section-title mb-2">Create personal vault</h2>
                        <p className="credential-vault-settings-section-lead mb-4">
                          <strong className="text-body">Important:</strong> This password is not stored on the server. If
                          you lose it, you will not be able to read your personal credentials. Choose a strong password and
                          confirm it below.
                        </p>
                        <BsForm onSubmit={handlePersonalVaultSetup} autoComplete="off">
                          {personalSetupError ? (
                            <div className="text-danger small mb-3">{personalSetupError}</div>
                          ) : null}
                          <Row>
                            <Col lg={6} className="mb-2">
                              <div className="form-group mb-3">
                                <label htmlFor="vault-setup-personal-pw" className="text-label">
                                  Personal password
                                </label>
                                <div className="position-relative password-input-wrapper">
                                  <BsForm.Control
                                    id="vault-setup-personal-pw"
                                    type={showPersonalSetupPw ? 'text' : 'password'}
                                    autoComplete="off"
                                    className="form-control"
                                    value={personalSetupPw}
                                    onChange={(ev) => setPersonalSetupPw(ev.target.value)}
                                    disabled={personalSetupBusy}
                                    placeholder="New personal password"
                                  />
                                  <button
                                    type="button"
                                    className="password-toggle-btn"
                                    disabled={personalSetupBusy}
                                    onClick={() => setShowPersonalSetupPw((v) => !v)}
                                    aria-label={showPersonalSetupPw ? 'Hide password' : 'Show password'}
                                  >
                                    <i
                                      className={`fas ${showPersonalSetupPw ? 'fa-eye-slash' : 'fa-eye'}`}
                                      aria-hidden
                                    />
                                  </button>
                                </div>
                              </div>
                            </Col>
                            <Col lg={6} className="mb-2">
                              <div className="form-group mb-3">
                                <label htmlFor="vault-setup-personal-pw2" className="text-label">
                                  Confirm personal password
                                </label>
                                <div className="position-relative password-input-wrapper">
                                  <BsForm.Control
                                    id="vault-setup-personal-pw2"
                                    type={showPersonalSetupPw2 ? 'text' : 'password'}
                                    autoComplete="off"
                                    className="form-control"
                                    value={personalSetupPw2}
                                    onChange={(ev) => setPersonalSetupPw2(ev.target.value)}
                                    disabled={personalSetupBusy}
                                    placeholder="Confirm new personal password"
                                  />
                                  <button
                                    type="button"
                                    className="password-toggle-btn"
                                    disabled={personalSetupBusy}
                                    onClick={() => setShowPersonalSetupPw2((v) => !v)}
                                    aria-label={showPersonalSetupPw2 ? 'Hide password' : 'Show password'}
                                  >
                                    <i
                                      className={`fas ${showPersonalSetupPw2 ? 'fa-eye-slash' : 'fa-eye'}`}
                                      aria-hidden
                                    />
                                  </button>
                                </div>
                              </div>
                            </Col>
                          </Row>
                          <div className="text-end toolbar toolbar-bottom pt-3">
                            <Button
                              type="submit"
                              className="btn credential-vault-settings-submit-btn"
                              disabled={personalSetupBusy}
                            >
                              {personalSetupBusy ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Creating…
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-key me-2" aria-hidden />
                                  Create personal vault
                                </>
                              )}
                            </Button>
                          </div>
                        </BsForm>
                      </>
                    )}
                  </section>
                ) : null}

                <section
                  className={
                    isCorporateAccount
                      ? 'credential-vault-settings-password-block credential-vault-settings-password-block--after-personal mt-4 pt-4 border-top mb-0'
                      : 'credential-vault-settings-password-block mb-0'
                  }
                >
                  {isCorporateAccount ? (
                    vaultInitialized ? (
                    <>
                      <h2 className="credential-vault-settings-section-title mb-2">Change corporate master password</h2>
                      <p className="credential-vault-settings-section-lead mb-4">
                        Enter your current master password, then choose a new one. You do not need to unlock the
                        Credentials Vault on this device first.
                      </p>
                      <Formik
                    initialValues={{
                      currentMasterPassword: '',
                      newMasterPassword: '',
                      confirmNewMasterPassword: '',
                    }}
                    validationSchema={VaultSettingsSchema}
                    onSubmit={async (values, { setSubmitting, resetForm }) => {
                      try {
                        const saltRes = await getVaultSalt();
                        if (!saltRes.data?.success) {
                          throw new Error(saltRes.data?.message || 'Could not load vault salt');
                        }
                        const kdfSalt = saltRes.data?.data?.kdfSalt;
                        if (!kdfSalt) {
                          throw new Error(saltRes.data?.message || 'Could not load vault salt');
                        }
                        const saltBytes = base64ToBytes(kdfSalt);
                        const { kek, authHash } = await deriveKeys(values.currentMasterPassword, saltBytes);
                        try {
                          const unlockRes = await unlockVaultApi(authHash);
                          if (!unlockRes.data?.success) {
                            throw new Error(unlockRes.data?.message || 'Invalid current master password');
                          }
                          const enc = unlockRes.data?.data?.encryptedDek;
                          if (!enc) {
                            throw new Error(unlockRes.data?.message || 'Unlock failed');
                          }
                          const rotation = await prepareMasterPasswordRotation(
                            values.currentMasterPassword,
                            values.newMasterPassword,
                            values.confirmNewMasterPassword,
                            kdfSalt,
                            enc
                          );
                          const resetRes = await resetMasterPasswordApi({
                            currentAuthHash: rotation.currentAuthHash,
                            newAuthHash: rotation.newAuthHash,
                            newEncryptedDek: rotation.newEncryptedDek,
                          });
                          if (!resetRes.data?.success) {
                            throw new Error(resetRes.data?.message || 'Failed to update master password');
                          }
                          toast.success('Master password updated');
                          resetForm();
                          await loadHistory();
                        } finally {
                          await zeroBytes(kek);
                        }
                      } catch (err) {
                        const msg =
                          err?.response?.data?.message || err?.message || 'Failed to reset master password';
                        toast.error(msg);
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({ isSubmitting }) => (
                      <FormikForm autoComplete="off">
                        <Row>
                          <Col md={12} className="mb-2">
                            <div className="form-group mb-3">
                              <label htmlFor="vault-current-master" className="text-label">
                                Current master password
                              </label>
                              <div className="position-relative password-input-wrapper">
                                <Field
                                  id="vault-current-master"
                                  type={showMasterCurrentPw ? 'text' : 'password'}
                                  name="currentMasterPassword"
                                  autoComplete="new-password"
                                  className="form-control"
                                  placeholder="Current master password"
                                />
                                <button
                                  type="button"
                                  className="password-toggle-btn"
                                  onClick={() => setShowMasterCurrentPw((v) => !v)}
                                  aria-label={showMasterCurrentPw ? 'Hide password' : 'Show password'}
                                  disabled={isSubmitting}
                                >
                                  <i
                                    className={`fas ${showMasterCurrentPw ? 'fa-eye-slash' : 'fa-eye'}`}
                                    aria-hidden
                                  />
                                </button>
                              </div>
                              <ErrorMessage
                                name="currentMasterPassword"
                                component="div"
                                className="text-danger small"
                              />
                            </div>
                          </Col>
                          <Col lg={6} className="mb-2">
                            <div className="form-group mb-3">
                              <label htmlFor="vault-new-master" className="text-label">
                                New master password
                              </label>
                              <div className="position-relative password-input-wrapper">
                                <Field
                                  id="vault-new-master"
                                  type={showMasterNewPw ? 'text' : 'password'}
                                  name="newMasterPassword"
                                  autoComplete="new-password"
                                  className="form-control"
                                  placeholder="New master password"
                                />
                                <button
                                  type="button"
                                  className="password-toggle-btn"
                                  onClick={() => setShowMasterNewPw((v) => !v)}
                                  aria-label={showMasterNewPw ? 'Hide password' : 'Show password'}
                                  disabled={isSubmitting}
                                >
                                  <i
                                    className={`fas ${showMasterNewPw ? 'fa-eye-slash' : 'fa-eye'}`}
                                    aria-hidden
                                  />
                                </button>
                              </div>
                              <ErrorMessage name="newMasterPassword" component="div" className="text-danger small" />
                            </div>
                          </Col>
                          <Col lg={6} className="mb-2">
                            <div className="form-group mb-3">
                              <label htmlFor="vault-confirm-master" className="text-label">
                                Confirm new master password
                              </label>
                              <div className="position-relative password-input-wrapper">
                                <Field
                                  id="vault-confirm-master"
                                  type={showMasterConfirmPw ? 'text' : 'password'}
                                  name="confirmNewMasterPassword"
                                  autoComplete="new-password"
                                  className="form-control"
                                  placeholder="Confirm new master password"
                                />
                                <button
                                  type="button"
                                  className="password-toggle-btn"
                                  onClick={() => setShowMasterConfirmPw((v) => !v)}
                                  aria-label={showMasterConfirmPw ? 'Hide password' : 'Show password'}
                                  disabled={isSubmitting}
                                >
                                  <i
                                    className={`fas ${showMasterConfirmPw ? 'fa-eye-slash' : 'fa-eye'}`}
                                    aria-hidden
                                  />
                                </button>
                              </div>
                              <ErrorMessage
                                name="confirmNewMasterPassword"
                                component="div"
                                className="text-danger small"
                              />
                            </div>
                          </Col>
                        </Row>
                        <div className="text-end toolbar toolbar-bottom pt-3">
                          <button
                            type="submit"
                            className="btn credential-vault-settings-submit-btn"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Updating…
                              </>
                            ) : (
                              <>
                                <i className="fas fa-key me-2" aria-hidden />
                                Update master password
                              </>
                            )}
                          </button>
                        </div>
                      </FormikForm>
                    )}
                  </Formik>
                    </>
                    ) : (
                      <Alert variant="light" className="credential-vault-settings-message mb-0">
                        The organization Credentials Vault is not initialized yet. Complete setup from the{' '}
                        <Link to="/credential-vault/credentialVaultList">Corporate credentials</Link> list. Personal vault
                        options remain available above.
                      </Alert>
                    )
                  ) : (
                    <Alert variant="light" className="credential-vault-settings-message mb-0">
                      The Credentials Vault master password can only be managed for corporate accounts.
                    </Alert>
                  )}
                </section>
              </Card.Body>
            </Card>
          </Col>

        {showHistoryColumn ? (
          <Col xl={4} lg={4} md={12}>
            <Card className="user-list-card credential-vault-log-card credential-vault-settings-history-card h-100">
              <Card.Header className="credential-vault-log-card-header">
                <Card.Title className="mb-0 credential-vault-log-card-title">History</Card.Title>
                <span className="credential-vault-log-card-hint">Changes to the Credentials Vault</span>
              </Card.Header>
              <Card.Body className="credential-vault-log-body credential-vault-settings-history-body">
                {historyLoading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" size="sm" role="status" />
                  </div>
                ) : historyError ? (
                  <p className="text-danger small mb-0">{historyError}</p>
                ) : historyRows.length === 0 ? (
                  <p className="text-muted small mb-0">No recorded changes yet.</p>
                ) : (
                  <ul className="credential-vault-activity-list list-unstyled mb-0">
                    {historyRows.map((row) => (
                      <li key={row.id} className="credential-vault-activity-item">
                        <div className="credential-vault-activity-title">{formatVaultCryptoActivity(row)}</div>
                        <div className="credential-vault-activity-meta text-muted small">
                          {row.performed_by_name || (row.performed_by ? `User #${row.performed_by}` : '—')}
                          {row.created_at ? ` · ${new Date(row.created_at).toLocaleString()}` : ''}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card.Body>
            </Card>
          </Col>
        ) : null}
      </Row>
    </div>
  );
}
